import { isVoiceSocketDebugEnabled } from '../../../../utils/voiceSocketDebug';

const LOG_PREFIX = '[voice-webrtc]';
const REDACTED = '[redacted]';
const CIRCULAR = '[Circular]';
const MAX_DEPTH = '[MaxDepth]';
const MAX_REDACT_DEPTH = 32;
const SECRET_KEY_PATTERN = /^(password|passwd|credential|token|authorization|secret)$/i;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isPlainLogObject(value) {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * @param {unknown} value
 * @param {WeakSet<object>} seen
 * @param {number} depth
 * @returns {unknown}
 */
function redactWebRtcLogValueInner(value, seen, depth) {
  if (depth > MAX_REDACT_DEPTH) {
    return MAX_DEPTH;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack
    };
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (!isPlainLogObject(value) && !Array.isArray(value)) {
    const label = value.constructor?.name || 'Object';
    return `[${label}]`;
  }

  if (seen.has(value)) {
    return CIRCULAR;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactWebRtcLogValueInner(item, seen, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => {
      if (SECRET_KEY_PATTERN.test(key)) {
        return [key, nested == null || nested === '' ? nested : REDACTED];
      }
      return [key, redactWebRtcLogValueInner(nested, seen, depth + 1)];
    })
  );
}

/**
 * @param {unknown} value
 * @returns {unknown}
 */
export function redactWebRtcLogValue(value) {
  return redactWebRtcLogValueInner(value, new WeakSet(), 0);
}

/**
 * Debug-gated WebRTC log. SIP passwords and ICE credentials are always redacted.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} [details]
 */
export function logWebRtcEvent(eventName, details = {}) {
  if (!isVoiceSocketDebugEnabled()) return;
  // Use console.log (not info) — Chrome often hides "Info" by default.
  // eslint-disable-next-line no-console
  console.log(LOG_PREFIX, eventName, redactWebRtcLogValue(details));
}

/**
 * Always-on failure log (ICE/media). Secrets still redacted.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} [details]
 */
export function logWebRtcFailure(eventName, details = {}) {
  // eslint-disable-next-line no-console
  console.warn(LOG_PREFIX, eventName, redactWebRtcLogValue(details));
}

function toLoggableArg(arg) {
  if (arg instanceof Error) return arg;
  return redactWebRtcLogValue(arg);
}

/**
 * @param {...unknown} args
 */
export function logWebRtcWarn(...args) {
  if (!isVoiceSocketDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.warn(LOG_PREFIX, ...args.map(toLoggableArg));
}

/**
 * Operational errors stay visible; payloads are still redacted.
 *
 * @param {...unknown} args
 */
export function logWebRtcError(...args) {
  // eslint-disable-next-line no-console
  console.error(LOG_PREFIX, ...args.map(toLoggableArg));
}
